import {
    type ComponentType,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from 'react';
import {
    EyeSlashIcon,
    ArrowsMergeIcon,
    PencilIcon,
    TrashIcon,
    type IconProps,
} from '@phosphor-icons/react';
import { Card } from '@/components/Card';
import { Button } from '@/components/ui/Button';
import { EntityActionsMenu } from '@/components/EntityActionsMenu';
import type { ActionMenuItem } from '@/components/EntityActionsMenu';
import { cn } from '@/lib/utils';

export interface DirectoryCardMetadataItem {
    icon: ComponentType<IconProps>;
    text: string;
    title?: string;
    textClassName?: string;
}

interface DirectoryCardActions {
    editLabel: ReactNode;
    deleteLabel?: ReactNode;
    markAsUnknownLabel?: ReactNode;
    mergeLabel?: ReactNode;
    onEdit?: () => void;
    onDelete?: () => void;
    onMarkAsUnknown?: () => void;
    onMerge?: () => void;
}

interface DirectoryCardProps {
    testId: string;
    title: string;
    image: ReactNode;
    metadata: DirectoryCardMetadataItem[];
    tagLabels: string[];
    isKnown: boolean;
    isDeleting?: boolean;
    disabled?: boolean;
    actions: DirectoryCardActions;
    markAsRecognizedLabel?: string;
    onClick?: (event?: MouseEvent<HTMLDivElement>) => void;
    onMarkAsRecognized?: () => void;
}

export function DirectoryCard({
    testId,
    title,
    image,
    metadata,
    tagLabels,
    isKnown,
    isDeleting,
    disabled,
    actions,
    markAsRecognizedLabel,
    onClick,
    onMarkAsRecognized,
}: DirectoryCardProps) {
    const isInteractionBlocked = isDeleting || disabled;

    const activate = (event?: MouseEvent<HTMLDivElement>) => {
        if (!isInteractionBlocked) onClick?.(event);
    };

    const onCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate();
        }
    };

    return (
        <div
            data-testid={testId}
            role="button"
            tabIndex={isInteractionBlocked ? -1 : 0}
            aria-disabled={isInteractionBlocked || undefined}
            onKeyDown={onCardKeyDown}
            className={cn(
                'w-full isolate relative transition-opacity duration-200 group rounded-[10px] outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-grey-700',
                isDeleting &&
                    'opacity-50 grayscale pointer-events-none cursor-default',
                !isDeleting &&
                    disabled &&
                    'opacity-60 pointer-events-none cursor-not-allowed',
                !isInteractionBlocked && 'cursor-pointer',
            )}
        >
            <Card
                orientation="vertical"
                size="narrow"
                className="group"
                title={title}
                thumbnailMedia={image}
                metadata={metadata}
                tags={tagLabels}
                onClick={activate}
                sideAction={
                    <EntityActionsMenu
                        wrapperClassName="flex-shrink-0 flex items-center"
                        triggerButtonClassName="h-6 w-6 p-0 flex items-center justify-center"
                        triggerIconClassName="w-5 h-5 text-grey-300 hover:text-white-100 transition-colors"
                        items={buildDirectoryCardMenuItems(actions)}
                    />
                }
            />

            {!isKnown && onMarkAsRecognized && markAsRecognizedLabel && (
                <Button
                    variant="secondary"
                    size="medium"
                    className="absolute left-3 right-3 bottom-2 z-10 hidden group-hover:flex group-focus-within:flex"
                    onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        onMarkAsRecognized();
                    }}
                >
                    {markAsRecognizedLabel}
                </Button>
            )}
        </div>
    );
}

function buildDirectoryCardMenuItems(
    actions: DirectoryCardActions,
): ActionMenuItem[] {
    const items: ActionMenuItem[] = [];
    if (actions.onEdit) {
        items.push({
            key: 'edit',
            label: actions.editLabel,
            icon: PencilIcon,
            onSelect: actions.onEdit,
        });
    }
    if (actions.onMarkAsUnknown && actions.markAsUnknownLabel) {
        items.push({
            key: 'markAsUnknown',
            label: actions.markAsUnknownLabel,
            icon: EyeSlashIcon,
            onSelect: actions.onMarkAsUnknown,
        });
    }
    if (actions.onMerge && actions.mergeLabel) {
        items.push({
            key: 'merge',
            label: actions.mergeLabel,
            icon: ArrowsMergeIcon,
            onSelect: actions.onMerge,
        });
    }
    if (actions.onDelete !== undefined && actions.deleteLabel) {
        items.push({
            key: 'delete',
            label: actions.deleteLabel,
            icon: TrashIcon,
            variant: 'danger',
            onSelect: actions.onDelete,
        });
    }
    return items;
}
